/**
 * The composite `address` form field: one labelled block with five sub-inputs,
 * stored as a structured object (rather than a positional array) so it reads
 * cleanly in HR exports and survives future sub-field additions.
 *
 * Shared by the builder, the inductee renderer, and the server validator.
 */

export interface AddressValue {
  line1: string;
  line2: string;
  city: string;
  county: string;
  postcode: string;
}

/** Sub-fields in display order, with their captions and whether they're needed
 * when the Address field as a whole is marked required. */
export const ADDRESS_SUBFIELDS = [
  { key: 'line1', label: '1st Line of Address', requiredPart: true },
  { key: 'line2', label: '2nd Line of Address', requiredPart: false },
  { key: 'city', label: 'City', requiredPart: true },
  { key: 'county', label: 'County', requiredPart: false },
  { key: 'postcode', label: 'Post Code', requiredPart: true },
] as const;

/** Coerce any stored value into a full AddressValue with string fields. */
export function asAddress(v: unknown): AddressValue {
  const o = (typeof v === 'object' && v !== null ? v : {}) as Record<string, unknown>;
  const s = (x: unknown): string => (typeof x === 'string' ? x : '');
  return {
    line1: s(o.line1),
    line2: s(o.line2),
    city: s(o.city),
    county: s(o.county),
    postcode: s(o.postcode),
  };
}

/**
 * A required Address is satisfied when the three load-bearing parts are
 * present: 1st line, City and Post Code. 2nd line and County stay optional.
 */
export function addressRequiredSatisfied(v: unknown): boolean {
  const a = asAddress(v);
  return a.line1.trim() !== '' && a.city.trim() !== '' && a.postcode.trim() !== '';
}
